import React, { useRef, useState, useEffect, useMemo } from "react";
import { Button, Box, Typography } from "@mui/material";
import { Download, Image } from "@mui/icons-material";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";
import jsPDF from "jspdf";
import { useQueryClient } from "@tanstack/react-query";
import { SchoolData } from "../../hooks/useSchools";
import { useAuth } from "../../contexts/AuthContext";
import { useSchools } from "../../hooks/useSchools";
import { useTeacher } from "../../hooks/useTeacher";
import { useStudentPhoto } from "../../hooks/useStudents";
import { useTranslation } from "react-i18next";
import { useVocationalSubjects, useSubjectMarksMappingByClassName } from "../../hooks/useSubjects";
import {
  formatBackendGradeForUi,
  formatTotalGradeForDisplay,
} from "../../utils/gradeDisplay";

const containerStyle = {
  background: "white",
  minHeight: "100vh",
  padding: "0",
  margin: "0",
  fontFamily: "Arial, sans-serif",
};

const mainBoxStyle = {
  background: "#fff",
  margin: "10px auto",
  width: "210mm",
  maxWidth: "210mm",
  minHeight: "297mm",
  position: "relative" as const,
  padding: "20px",
  boxSizing: "border-box" as const,
  // Use a single border (outline causes double borders in PDF capture)
  border: "2px solid #000",
};

// Official Board Header Styles
const boardHeaderStyle = {
  textAlign: "center" as const,
  marginBottom: "20px",
  borderBottom: "2px solid #000",
  paddingBottom: "15px",
};

const boardNameStyle = {
  fontSize: "24px",
  fontWeight: "bold",
  color: "#000000",
  margin: "0 0 10px 0",
  textTransform: "uppercase" as const,
};

const examTitleStyle = {
  fontSize: "18px",
  fontWeight: "bold",
  color: "#000",
  margin: "0 0 5px 0",
  textTransform: "uppercase" as const,
};

const examDateStyle = {
  fontSize: "14px",
  fontWeight: "bold",
  color: "#000",
  margin: "0 0 10px 0",
  textAlign: "right" as const,
};

const documentTypeStyle = {
  fontSize: "16px",
  fontWeight: "bold",
  color: "#000",
  margin: "0 0 10px 0",
  textTransform: "uppercase" as const,
};

const serialNumberStyle = {
  fontSize: "14px",
  fontWeight: "bold",
  color: "#000",
  margin: "0",
  textAlign: "right" as const,
};

// Student Details Section
const studentDetailsStyle = {
  // margin: "20px 0",
  padding: "15px",
};

const studentInfoRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "15px",
};

const studentPhotoStyle = {
  width: "130px",
  height: "150px",
  border: "1px solid black",
  // borderRadius: "4px",
  objectFit: "fill" as const,
  marginLeft: "20px",
};

const studentPhotoContainerStyle = {
  display: "flex",
  height: "120px",
  flexDirection: "column" as const,
  alignItems: "center",
  marginLeft: "10px",
  marginBottom: "20px",
};

const studentNameBelowPhotoStyle = {
  fontSize: "12px",
  fontWeight: "bold",
  color: "#000",
  marginTop: "5px",
  textAlign: "center" as const,
  textTransform: "uppercase" as const,
};

const certificationTextStyle = {
  fontSize: "14px",
  fontWeight: "bold",
  color: "#000",
  margin: "10px 0",
  textAlign: "center" as const,
};

const studentDetailsTableStyle = {
  width: "100%",
  borderCollapse: "collapse" as const,
  fontSize: "12px",
  // margin: "5px 0",
};

const studentDetailsThStyle = {
  border: "1px solid #000",
  background: "#e9ecef",
  padding: "8px 4px",
  textAlign: "center" as const,
  fontWeight: "bold",
  fontSize: "11px",
};

const studentDetailsTdStyle = {
  border: "1px solid #000",
  padding: "8px 4px",
  textAlign: "center" as const,
  fontSize: "11px",
};

// School Details Table Styles
const schoolDetailsTableStyle = {
  width: "100%",
  borderCollapse: "collapse" as const,
  fontSize: "12px",
  margin: "20px 0",
  border: "1px solid #000",
};

const schoolDetailsThStyle = {
  border: "1px solid #000",
  background: "#e9ecef",
  padding: "8px 4px",
  textAlign: "center" as const,
  fontWeight: "bold",
  fontSize: "14px",
};

const schoolDetailsTdStyle = {
  border: "1px solid #000",
  padding: "8px 4px",
  textAlign: "center" as const,
  fontSize: "14px",
};

// Marks Table Styles
const marksTableStyle = {
  width: "100%",
  borderCollapse: "collapse" as const,
  fontSize: "18px",
  // margin: "20px 5px",
  border: "1px solid #000",
};

const marksThStyle = {
  border: "1px solid #000",
  background: "#e9ecef",
  padding: "12px 6px",
  textAlign: "center" as const,
  fontWeight: "bold",
  fontSize: "11px",
};

const marksTdStyle = {
  border: "1px solid #000",
  padding: "10px 5px",
  textAlign: "center" as const,
  fontSize: "11px",
};

const subjectTdStyle = {
  border: "1px solid #000",
  padding: "6px 3px",
  textAlign: "left" as const,
  fontSize: "11px",
  fontWeight: "bold",
};

const totalRowStyle = {
  background: "#f8f9fa",
  fontWeight: "bold",
  color:"red",
};

// Summary Table Styles
const summaryTableStyle = {
  width: "100%",
  borderCollapse: "collapse" as const,
  fontSize: "11px",
  margin: "20px 0",
  border: "1px solid #000",
};

const summaryThTdStyle = {
  border: "1px solid #000",
  padding: "8px 4px",
  textAlign: "center" as const,
  fontWeight: "bold",
  fontSize: "14px",
};

// Signature Styles
const signatureStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  margin: "65px 45px 20px 45px",
  fontSize: "16px",
  fontWeight: "bold",
};

const signatureStyleCompact = {
  ...signatureStyle,
  margin: "30px 20px 10px 20px",
  fontSize: "14px",
};

const signatureBlockStyle = {
  textAlign: "center" as const,
  minWidth: "150px",
};

const teacherSignatureStyle = {
  ...signatureBlockStyle,
  color: "#3a5ca8",
};

const principalSignatureStyle = {
  ...signatureBlockStyle,
  color: "#b22222",
};

// Download Button Styles
const downloadButtonStyle = {
  background: "#5fa8e6",
  color: "#fff",
  border: "2px solid #fff",
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "14px",
  fontWeight: "bold",
  margin: "20px 10px",
  cursor: "pointer",
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
};

interface Subject {
  subjectCode: string;
  subjectName: string;
  theoryMarks: number;
  practicalMarks: number;
  totalMarks?: number;
  grade?: string;
  remark?: string;
  quarterlyWeightage?: number; // Double in backend (nullable)
  halfYearlyWeightage?: number; // Double in backend (nullable)
  originalPracticalMarks?: number; // Integer in backend (nullable)
  quarterlyTotal?: number | null; // Total from QUARTERLY marksheet
  halfYearlyTotal?: number | null; // Total from HALF_YEARLY marksheet
  quarterlyTheoryMarks?: number | null; // Theory marks from QUARTERLY marksheet
  quarterlyPracticalMarks?: number | null; // Practical marks from QUARTERLY marksheet
  halfYearlyTheoryMarks?: number | null; // Theory marks from HALF_YEARLY marksheet
  halfYearlyPracticalMarks?: number | null; // Practical marks from HALF_YEARLY marksheet
  groupName?: string; // Group name (e.g., "NSQF GROUP", "ADDITIONAL GROUP")
}

/**
 * Match marksheet subject rows to subject-mark-mapping (priority, max marks, group).
 * Board codes are often zero-padded (e.g. "051"); marksheet payloads may send "51".
 */
function normalizeSubjectCodeForMapping(code: unknown): string {
  const s = String(code ?? "").trim();
  if (!s) return "";
  if (/^\d+$/.test(s)) {
    return s.padStart(3, "0");
  }
  return s.toUpperCase();
}

interface Student {
  id?: number; // Student ID for fetching photo
  rollNumber: string;
  enrolmentNo: string;
  studentName?: string; // New field for combined name
  firstName?: string; // Keep for backward compatibility
  lastName?: string; // Keep for backward compatibility
  fatherName: string;
  motherName: string;
  dateOfBirth: string | number[]; // Support both string and array formats
  gender: string;
  medium: string;
  caste: string;
  section?: string;
  className?: string;
  photo?: string; // Student photo URL
}

interface MarksheetTemplateProps {
  student: Student;
  schoolName: string;
  schoolCode: string;
  diseCode: string;
  marksheetType: string;
  sessionYear: string;
  className: string;
  subjects: Subject[];
  percentage: number;
  totalGrade: string; // Added totalGrade prop
  result: string;
  rank: number;
  failSubjects: string;
  teacherId?: number; // Changed to teacherId for dynamic fetching
  onPrint?: () => void;
  isPdf?: boolean;
  hideDownloadButton?: boolean;
  school?: SchoolData; // Add school prop for logo fetching
  totalTheoryObtained?: number; // Total theory marks from response
  totalPracticalObtained?: number; // Total practical marks from response
  totalObtained?: number; // Total obtained marks from response
  classId?: number; // Class ID for fetching vocational subjects
  sessionId?: number; // Session ID for fetching staff by session and class
}

const MarksheetTemplate: React.FC<MarksheetTemplateProps> = ({
  student,
  schoolName,
  schoolCode,
  diseCode,
  marksheetType,
  sessionYear,
  className,
  subjects: rawSubjects,
  percentage,
  totalGrade,
  result,
  rank,
  failSubjects,
  teacherId,
  onPrint,
  isPdf = false,
  hideDownloadButton = false,
  school,
  totalTheoryObtained,
  totalPracticalObtained,
  totalObtained,
  classId,
  sessionId,
}) => {
  const { t } = useTranslation();
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdfFromImage, setIsDownloadingPdfFromImage] =
    useState(false);
  const displayMarksheetType =
    marksheetType === "ANNUAL" &&
    !String(className || "").toLowerCase().includes("11th")
      ? "FINAL"
      : marksheetType;
  const [schoolLogoUrl, setSchoolLogoUrl] = useState<string | null>(null);
  const templateRef = useRef<HTMLDivElement>(null);

  // Get user and school data for logo fetching
  const { user } = useAuth();
  const { useSchoolLogoInfo, useSchoolById } = useSchools();
  const { useTeacherById, useTeachersBySchool, useStaffByStaffAndClass, useStaffClassMapping } = useTeacher();
  const queryClient = useQueryClient();
  const schoolId = user?.schoolId ?? (school?.id ?? 1);

  // Fetch vocational subjects for the class
  const { data: vocationalSubjects } = useVocationalSubjects(classId);

  // Helper function to extract base class name (e.g., "11th" from "11th A")
  const getBaseClassName = (className: string): string => {
    if (!className) return "";
    // Match class name pattern like "11th", "10th", "12th", etc.
    const match = className.match(/^(\d+(?:th|st|nd|rd)?)/i);
    return match ? match[1] : className.split(/\s+/)[0]; // Fallback to first word
  };

  // Base class for mapping API (prop may be empty while student.className is set, e.g. view dialog)
  const baseClassName = getBaseClassName(
    (className || student.className || "").trim()
  );

  const classWithSectionDisplay = useMemo(() => {
    const clsRaw = (className || student.className || "").trim();
    const secRaw = (student.section || "").trim();
 
    if (!clsRaw && !secRaw) return "N/A";
    if (!secRaw) return clsRaw || "N/A";
 
    const clsLower = clsRaw.toLowerCase();
    const secLower = secRaw.toLowerCase();
    const hasSectionAlready =
      clsLower === secLower ||
      clsLower.includes(` ${secLower}`) ||
      clsLower.includes(`-${secLower}`) ||
      clsLower.includes(`(${secLower}`) ||
      clsLower.includes(` ${secLower} `);
 
    return hasSectionAlready ? clsRaw : `${clsRaw} - ${secRaw}`;
  }, [className, student.className, student.section]);

  const is6thOr7th = useMemo(() => {
    const base = baseClassName.toLowerCase();
    return base.includes("6th") || base.includes("7th");
  }, [baseClassName]);

  // Fetch subject marks mapping to get max/min marks, groupName, and display priority (all classes)
  const { data: subjectMarksMapping } = useSubjectMarksMappingByClassName(
    baseClassName || undefined
  );

  // subjectCode (normalized) -> priorityNo from API (lower comes first)
  const subjectPriorityMap = useMemo(() => {
    const map = new Map<string, number>();
    (subjectMarksMapping || []).forEach((m: any) => {
      const key = normalizeSubjectCodeForMapping(m?.subjectCode);
      if (!key) return;
      const p =
        m?.priorityNo ??
        m?.priority_no ??
        m?.priority ??
        m?.priorityNumber;
      const n = Number(p);
      if (!Number.isFinite(n)) return;
      map.set(key, n);
    });
    return map;
  }, [subjectMarksMapping]);

  // subjectCode -> full mapping (needed before subject order: group + priority)
  const subjectMarksConfigMap = useMemo(() => {
    const map = new Map<string, any>();
    if (subjectMarksMapping) {
      subjectMarksMapping.forEach((mapping: any) => {
        const key = normalizeSubjectCodeForMapping(mapping?.subjectCode);
        if (key) {
          map.set(key, mapping);
        }
      });
    }
    return map;
  }, [subjectMarksMapping]);

  const subjectGroupMap = useMemo(() => {
    const map = new Map<string, string>();
    subjectMarksConfigMap.forEach((mapping, code) => {
      if (mapping.groupName) {
        map.set(code, mapping.groupName);
      }
    });
    return map;
  }, [subjectMarksConfigMap]);

  // All classes, all marksheet types: order subjects by subject-mark-mapping priorityNo (lower first).
  const subjects = useMemo(() => {
    const list = Array.isArray(rawSubjects) ? [...rawSubjects] : [];
    const mappingReady = subjectMarksMapping != null;
    const shouldOrderByMapping = mappingReady && subjectPriorityMap.size > 0;

    if (!shouldOrderByMapping) {
      return list;
    }

    const decorated = list.map((s, idx) => ({ s, idx }));

    decorated.sort((a, b) => {
      const sa = a.s as Subject;
      const sb = b.s as Subject;

      const aKey = normalizeSubjectCodeForMapping(sa.subjectCode);
      const bKey = normalizeSubjectCodeForMapping(sb.subjectCode);
      const aPri = aKey ? subjectPriorityMap.get(aKey) : undefined;
      const bPri = bKey ? subjectPriorityMap.get(bKey) : undefined;

      const aHas = typeof aPri === "number";
      const bHas = typeof bPri === "number";
      if (aHas && bHas) {
        if (aPri !== bPri) return aPri! - bPri!;
        return aKey.localeCompare(bKey);
      }
      if (aHas) return -1;
      if (bHas) return 1;
      return a.idx - b.idx;
    });

    return decorated.map((d) => d.s);
  }, [
    rawSubjects,
    subjectPriorityMap,
    subjectMarksMapping,
  ]);

  // Use school prop if provided, otherwise fetch from API
  const schoolData = school || null;

  // Only fetch logo info if school prop is not provided
  const { data: logoInfo, isLoading: logoLoading } = useSchoolLogoInfo(
    school ? school.id : schoolId
  );

  // Fetch school data for principal name only if not provided via prop
  const { data: fetchedSchoolData, isLoading: fetchedSchoolLoading } =
    useSchoolById(school ? undefined : schoolId);
  const finalSchoolData = schoolData || fetchedSchoolData;
  const finalSchoolLoading = school ? false : fetchedSchoolLoading;

  // Fetch all teachers for the school to filter by class
  const { data: allTeachers } = useTeachersBySchool(schoolId);

  // Fetch staff by session and class using new API
  const { data: staffBySessionAndClass } = useStaffByStaffAndClass(sessionId, classId);

  // Fetch staff class mapping by session using new API
  const { data: staffClassMappings } = useStaffClassMapping(sessionId);

  // Fetch teacher data if teacherId is provided
  const {
    data: teacherDataById,
    isLoading: teacherLoading,
    error: teacherError,
  } = useTeacherById(teacherId || 0);

  // Filter teachers by class to ensure only class-specific teacher is shown
  const getClassSpecificTeacher = useMemo(() => {
    // Priority 1: Use staff from new API (getByStaffAndClass) - get first teacher from the list
    if (staffBySessionAndClass && staffBySessionAndClass.length > 0) {
      const firstStaff = staffBySessionAndClass[0];
      // Check if staff is active
      const isActive = firstStaff.isActive !== false;
      const hasStatus = firstStaff.status !== false;
      if (isActive && hasStatus) {
        return firstStaff;
      }
    }

    // Priority 2: Use existing logic for backward compatibility
    if (!className || !allTeachers || allTeachers.length === 0) {
      return teacherDataById;
    }

    const normalizedClassName = className.trim().toLowerCase();

    // Filter active teachers assigned to this class
    const classTeachers = allTeachers.filter((teacher) => {
      // Check if teacher is active
      const isActive = teacher.isActive !== false;
      const hasStatus = teacher.status !== false;
      if (!isActive || !hasStatus) return false;

      // Check if teacher is assigned to this class
      if (teacher.teacherOfClass && Array.isArray(teacher.teacherOfClass)) {
        return teacher.teacherOfClass.some((assignedClass: string) => {
          const normalizedAssignedClass = assignedClass.trim().toLowerCase();
          // Check for exact match or partial match
          return normalizedAssignedClass === normalizedClassName ||
            normalizedClassName.includes(normalizedAssignedClass) ||
            normalizedAssignedClass.includes(normalizedClassName);
        });
      }
      return false;
    });

    // If we have a teacherId and it matches a class teacher, use it
    if (teacherId && teacherDataById) {
      const isClassTeacher = classTeachers.some(t => t.id === teacherId);
      if (isClassTeacher) {
        return teacherDataById;
      }
    }

    // Otherwise, use the first class-specific teacher
    if (classTeachers.length > 0) {
      // If we have the teacher data already fetched, use it, otherwise return the teacher object
      const selectedTeacher = classTeachers[0];
      if (selectedTeacher.id === teacherId && teacherDataById) {
        return teacherDataById;
      }
      // Return the teacher object (we'll use firstName/lastName directly)
      return selectedTeacher;
    }

    // Fallback to teacherDataById if no class-specific teacher found
    return teacherDataById;
  }, [staffBySessionAndClass, className, allTeachers, teacherId, teacherDataById]);

  // Use class-specific teacher data
  const teacherData = getClassSpecificTeacher;

  // Fetch student photo using the useStudentPhoto hook
  const studentId = student?.id;
  const {
    imageUrl: authenticatedImageUrl,
    isLoading: photoLoading,
    error: imageError,
    hasPhoto,
  } = useStudentPhoto(studentId);

  // Clear teacher cache when component mounts to ensure fresh data
  useEffect(() => {
    if (teacherId) {
      // Force refetch of teacher data to ensure we have the latest status
      queryClient.invalidateQueries({ queryKey: ["teacher", teacherId] });
    }
  }, [teacherId, queryClient]);

  // Helper function to get teacher name
  const getTeacherName = () => {
    // If teacher is loading and we have a teacherId, wait for it
    if (teacherLoading && teacherId) {
      return t("teacher_name");
    }

    // Priority 1: Use staff from new API (staffBySessionAndClass[0])
    if (staffBySessionAndClass && staffBySessionAndClass.length > 0) {
      const firstStaff = staffBySessionAndClass[0];
      const firstName = firstStaff.firstName || "";
      const lastName = firstStaff.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim() || t("teacher_name");

      // Find class mapping for this specific teacher in this session
      let teacherMappedClass = "";
      if (staffClassMappings && staffClassMappings.length > 0) {
        // Filter mappings for this teacher and possibly this classId
        const mapping = staffClassMappings.find(m =>
          (m.staffId === firstStaff.id || m.teacherId === firstStaff.id) &&
          (classId ? m.classId === classId : true)
        );

        if (mapping) {
          teacherMappedClass = mapping.className || (mapping.classId ? `Class ${mapping.classId}` : "");
          if (mapping.section) teacherMappedClass += ` - ${mapping.section}`;
        }
      }

      if (teacherMappedClass) {
        return `${fullName}`;
      }
      return fullName;
    }

    // Priority 2: Use teacherData (either from teacherId or filtered by allTeachers), fallback logic
    if (teacherData) {
      const isActive = teacherData.isActive !== false;
      const hasStatus = teacherData.status !== false;

      if (!isActive || !hasStatus) {
        return t("teacher_name");
      }

      const firstName = teacherData.firstName || "";
      const lastName = teacherData.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim() || t("teacher_name");
      return fullName;
    }

    // Final fallback
    return t("teacher_name");
  };

  // Helper function to get principal name
  const getPrincipalName = () => {
    // If we have school data, use it immediately
    if (finalSchoolData && finalSchoolData.schoolPrincipalName) {
      return finalSchoolData.schoolPrincipalName;
    }

    // If still loading and no data yet, show loading
    if (finalSchoolLoading) {
      return "Loading...";
    }

    // If no data available after loading, show fallback
    return "Principal Name Not Available";
  };

  // Effect to handle logo fetching with localStorage caching
  useEffect(() => {
    const fetchSchoolLogo = async () => {
      try {
        // If we have logoInfo, always use the latest logo (don't rely on cache for updates)
        if (logoInfo && logoInfo.fileName) {
          const logoUrl = `${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`;
          setSchoolLogoUrl(logoUrl);

          // Update cache with latest logo
          const currentSchoolId = school?.id || schoolId;
          const cachedLogoKey = `school_logo_${currentSchoolId}`;
          localStorage.setItem(cachedLogoKey, logoUrl);
          return;
        }

        // Fallback: check localStorage for cached logo only if no logoInfo
        const currentSchoolId = school?.id || schoolId;
        const cachedLogoKey = `school_logo_${currentSchoolId}`;
        const cachedLogo = localStorage.getItem(cachedLogoKey);

        if (cachedLogo) {
          setSchoolLogoUrl(cachedLogo);
          return;
        }

        // Final fallback to default logo
        setSchoolLogoUrl("/logo.png");
      } catch (error) {
        // Fallback to default logo
        setSchoolLogoUrl("/logo.png");
      }
    };

    const currentSchoolId = school?.id || schoolId;
    if (currentSchoolId && !logoLoading) {
      fetchSchoolLogo();
    }
  }, [school?.id, schoolId, logoInfo, logoLoading]);

  // Helper function to format date of birth
  const formatDateOfBirth = (dateOfBirth: string | number[]): string => {
    if (typeof dateOfBirth === "string") {
      return dateOfBirth;
    }

    if (Array.isArray(dateOfBirth) && dateOfBirth.length >= 3) {
      const [year, month, day] = dateOfBirth;
      // Format as DD/MM/YYYY
      const dayStr = String(day).padStart(2, "0");
      const monthStr = String(month).padStart(2, "0");
      return `${dayStr}/${monthStr}/${year}`;
    }

    return "N/A";
  };

  // Helper function to render marks with absent handling
  const renderMark = (mark: number | undefined, isAbsent: boolean = false) => {
    if (mark === -1 || mark === null || mark === undefined || isAbsent) {
      return <span style={{ color: "red", fontWeight: "bold" }}>ABS</span>;
    }
    return mark;
  };

  // Helper function to get custom max marks for specific subjects
  const getCustomMaxMarks = (subjectName: string, marksheetType: string, subjectCode?: string) => {
    // Attempt to get marks from API mapping
    const mapping = subjectCode
      ? subjectMarksConfigMap.get(normalizeSubjectCodeForMapping(subjectCode))
      : null;

    // Extracted marks from API mapping
    const mappedTheory = mapping?.theoryMarks ?? mapping?.maxTheoryMarks;
    const mappedPractical = mapping?.practicalMarks ?? mapping?.maxPracticalMarks;
    const mappedTotal = mapping?.totalMarks ?? mapping?.maxTotalMarks;
    const mappedMinTheory = mapping?.thPassMarks;
    const mappedMinPractical = mapping?.prPassMarks;

    // Helper to check if class is 1st-8th
    const numMatch = baseClassName.match(/\d+/);
    const classNum = numMatch ? parseInt(numMatch[0], 10) : 0;
    const is1stTo8th = classNum >= 1 && classNum <= 8;

    // Monthly always 20
    if (marksheetType === "MONTHLY") {
      return {
        theory: 20,
        practical: 0,
        total: 20,
        minTheory: mappedMinTheory != null ? Number(mappedMinTheory) : null,
        minPractical: mappedMinPractical != null ? Number(mappedMinPractical) : null
      };
    }

    // If API mapping exists for ANY class, use it as priority
    if (mappedTheory != null || mappedPractical != null || mappedTotal != null) {
      const theory = Number(mappedTheory ?? 0);
      const practical = Number(mappedPractical ?? 0);
      // Use mapped total if available, otherwise sum theory and practical
      const total = mappedTotal != null ? Number(mappedTotal) : (theory + practical);

      return {
        theory,
        practical,
        total,
        minTheory: mappedMinTheory != null ? Number(mappedMinTheory) : null,
        minPractical: mappedMinPractical != null ? Number(mappedMinPractical) : null
      };
    }

    // Fallbacks if no mapping is found in API
    if (is1stTo8th) {
      return {
        theory: 60,
        practical: 20,
        total: 80,
        minTheory: null,
        minPractical: null
      };
    }

    // Check if subject is vocational using API data
    const normalizedSubjectName = subjectName.toLowerCase().trim();
    const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;

    // For non-monthly marksheets, use 40:60 for vocational subjects, 75:25 for others
    if (isVocationalSubject) {
      return { theory: 40, practical: 60, total: 100, minTheory: null, minPractical: null };
    }

    // Default max marks for non-vocational subjects
    return { theory: 75, practical: 25, total: 100, minTheory: null, minPractical: null };
  };

  // Helper function to render total marks for non-Monthly types
  const renderTotalMark = (
    theoryMarks: number | undefined,
    practicalMarks: number | undefined,
    totalMarks?: number
  ) => {

    // If either theory or practical is -1, show A in red
    if (
      theoryMarks === -1 ||
      theoryMarks === null ||
      theoryMarks === undefined ||
      practicalMarks === -1 ||
      practicalMarks === null ||
      practicalMarks === undefined
    ) {
      return <span style={{ color: "red", fontWeight: "bold" }}>ABS</span>;
    }

    // Calculate total from theory + practical
    const calculatedTotal = (theoryMarks || 0) + (practicalMarks || 0);
    return calculatedTotal;
  };

  // Calculate totals using custom max marks (only excluding subjects with "#" in remarks, include ABS subjects)
  const totalTheoryMax = subjects.reduce((sum, s) => {
    // Only skip subjects with "#" in remarks, include ABS subjects in maximum marks calculation
    if (s.remark && s.remark.includes("#")) {
      return sum;
    }
    const maxMarks = getCustomMaxMarks(s.subjectName, marksheetType, s.subjectCode);
    return sum + maxMarks.theory;
  }, 0);

  const totalPracticalMax = subjects.reduce((sum, s) => {
    // Only skip subjects with "#" in remarks, include ABS subjects in maximum marks calculation
    if (s.remark && s.remark.includes("#")) {
      return sum;
    }
    const maxMarks = getCustomMaxMarks(s.subjectName, marksheetType, s.subjectCode);
    return sum + maxMarks.practical;
  }, 0);

  const totalMax = subjects.reduce((sum, s) => {
    // Only skip subjects with "#" in remarks, include ABS subjects in maximum marks calculation
    if (s.remark && s.remark.includes("#")) {
      return sum;
    }
    const maxMarks = getCustomMaxMarks(s.subjectName, marksheetType, s.subjectCode);
    return sum + maxMarks.total;
  }, 0);

  const totalTheory = subjects.reduce((sum, s) => {
    // Skip subjects with "#" or "ABS" in remarks
    if (s.remark && (s.remark.includes("#") || s.remark.includes("ABS"))) {
      return sum;
    }
    return (
      sum + (s.theoryMarks !== -1 && s.theoryMarks != null ? s.theoryMarks : 0)
    );
  }, 0);

  const totalPractical = subjects.reduce((sum, s) => {
    // Skip subjects with "#" or "ABS" in remarks
    if (s.remark && (s.remark.includes("#") || s.remark.includes("ABS"))) {
      return sum;
    }
    return (
      sum +
      (s.practicalMarks !== -1 && s.practicalMarks != null
        ? s.practicalMarks
        : 0)
    );
  }, 0);

  const totalObt = subjects.reduce((sum, s) => {
    // Skip subjects with "#" or "ABS" in remarks
    if (s.remark && (s.remark.includes("#") || s.remark.includes("ABS"))) {
      return sum;
    }
    const theoryMarks =
      s.theoryMarks !== -1 && s.theoryMarks != null ? s.theoryMarks : 0;
    const practicalMarks =
      s.practicalMarks !== -1 && s.practicalMarks != null
        ? s.practicalMarks
        : 0;
    return sum + (theoryMarks + practicalMarks);
  }, 0);

  // Calculate total quarterly and half-yearly from quarterlyTotal and halfYearlyTotal properties
  // These are set by buildFinalSubjectsWithExamTotals from QUARTERLY and HALF_YEARLY marksheets
  // Exclude vocational subjects
  const totalQuarterly = subjects.reduce((sum, s) => {
    const quarterlyTotal = (s as any).quarterlyTotal;
    if (quarterlyTotal == null || Number.isNaN(Number(quarterlyTotal))) {
      return sum;
    }
    // Check if subject is vocational
    const normalizedSubjectName = s.subjectName?.toLowerCase().trim() || "";
    const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
    if (isVocationalSubject) {
      return sum;
    }
    return sum + Number(quarterlyTotal);
  }, 0);

  const totalHalfYearly = subjects.reduce((sum, s) => {
    const halfYearlyTotal = (s as any).halfYearlyTotal;
    if (halfYearlyTotal == null || Number.isNaN(Number(halfYearlyTotal))) {
      return sum;
    }
    // Check if subject is vocational
    const normalizedSubjectName = s.subjectName?.toLowerCase().trim() || "";
    const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
    if (isVocationalSubject) {
      return sum;
    }
    return sum + Number(halfYearlyTotal);
  }, 0);

  subjects.forEach((subj) => {
    if (subj.grade) {
      subj.grade = formatBackendGradeForUi(subj.grade);
    }
  });

  // Function to download template as image
  const downloadAsImage = async () => {
    if (!templateRef.current) return;

    setIsDownloadingImage(true);
    try {
      // Wait a bit for any async content to load
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(templateRef.current, {
        useCORS: true,
        allowTaint: false,
        background: null, // Use null to preserve transparency and show logo/gradient
        scale: 1.2, // Reduced scale for smaller file size (was 2)
        width: templateRef.current.scrollWidth,
        height: templateRef.current.scrollHeight,
        windowWidth: templateRef.current.scrollWidth,
        windowHeight: templateRef.current.scrollHeight,
        logging: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc: Document) => {
          // Only set white background on the outer container, not inner elements
          const clonedElement = clonedDoc.body.firstElementChild as HTMLElement;
          if (clonedElement) {
            clonedElement.style.backgroundColor = "#ffffff";
            // Don't override inner container backgrounds to preserve logo/gradient
          }
        },
      } as any);

      const link = document.createElement("a");
      link.download = `marksheet-${student.firstName || student.studentName || "student"
        }-${student.lastName || ""}-${className}-${sessionYear}-${new Date().toISOString().split("T")[0]
        }.png`;
      link.href = canvas.toDataURL("image/png", 0.92); // Reduced quality for smaller file size
      link.click();
    } catch (error) {
      alert(t("failed_to_download_image"));
    } finally {
      setIsDownloadingImage(false);
    }
  };

  // Function to download template as PDF from image
  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) {
      alert(t("template_not_ready"));
      return;
    }

    setIsDownloadingPdfFromImage(true);
    try {
      const element = templateRef.current;
      // Try method 1: Direct jsPDF approach
      try {
        // Wait a bit for any async content to load
        await new Promise((resolve) => setTimeout(resolve, 300));

        // First capture with html2canvas for better quality
        const canvas = await html2canvas(element, {
          useCORS: true,
          allowTaint: false,
          background: null, // Use null to preserve transparency and show logo/gradient
          scale: 1.2, // Reduced scale for smaller file size (was 2)
          // Use offset* to ensure borders are included in capture
          width: element.offsetWidth,
          height: element.offsetHeight,
          windowWidth: element.offsetWidth,
          windowHeight: element.offsetHeight,
          logging: false,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: (clonedDoc: Document) => {
            // Only set white background on the outer container, not inner elements
            const clonedElement = clonedDoc.body.firstElementChild as HTMLElement;
            if (clonedElement) {
              clonedElement.style.backgroundColor = "#ffffff";
              // Don't override inner container backgrounds to preserve logo/gradient
            }
          },
        } as any);

        // Use PNG for crisp borders/lines in PDF (JPEG can introduce border artifacts)
        const imgData = canvas.toDataURL("image/png");

        // Create PDF with proper dimensions using imported jsPDF
        const pdf = new jsPDF({
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true, // Enable compression for smaller file size
        });

        // Get A4 dimensions in mm
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Fit the captured image into a single A4 page (prevents cutting off signatures).
        const margin = 5;
        // Inset a bit to avoid PDF edge clipping of the border
        const inset = 0.5;
        const maxWidth = pageWidth - (margin + inset) * 2;
        const maxHeight = pageHeight - (margin + inset) * 2;

        const naturalWidth = maxWidth;
        const naturalHeight = (canvas.height * naturalWidth) / canvas.width;

        const scale = naturalHeight > maxHeight ? maxHeight / naturalHeight : 1;
        const renderWidth = naturalWidth * scale;
        const renderHeight = naturalHeight * scale;

        const x = (pageWidth - renderWidth) / 2;
        const y = margin + inset;

        pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);

        // Save the PDF
        const fileName = `marksheet-${student.firstName || student.studentName || "student"
          }-${student.lastName || ""}-${className}-${sessionYear}-${new Date().toISOString().split("T")[0]
          }.pdf`;
        pdf.save(fileName);

        return;
      } catch (jsPDFError) {
        // Fallback method: Use html2pdf.js
        const opt = {
          margin: [0.1, 0.1, 0.1, 0.1],
          filename: `marksheet-${student.firstName || student.studentName || "student"
            }-${student.lastName || ""}-${className}-${sessionYear}-${new Date().toISOString().split("T")[0]
            }.pdf`,
          image: { type: "jpeg", quality: 0.85 }, // Use JPEG with reduced quality for smaller file size
          html2canvas: {
            scale: 1.2, // Reduced scale for smaller file size (was 2)
            useCORS: true,
            allowTaint: false,
            background: null, // Use null to preserve transparency and show logo/gradient
            width: element.scrollWidth,
            height: element.scrollHeight,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
            scrollX: 0,
            scrollY: 0,
            logging: false,
            removeContainer: false,
            imageTimeout: 15000,
            onclone: (clonedDoc: Document) => {
              // Only set white background on the outer container, not inner elements
              const clonedElement = clonedDoc.body.firstElementChild as HTMLElement;
              if (clonedElement) {
                clonedElement.style.backgroundColor = "#ffffff";
                // Don't override inner container backgrounds to preserve logo/gradient
              }
            },
          } as any,
          jsPDF: {
            unit: "in",
            format: "a4",
            orientation: "portrait",
            compress: false,
            putOnlyUsedFonts: false,
          },
          pagebreak: { mode: "avoid-all", before: ".page-break" },
        };

        await html2pdf().set(opt).from(element).save();
      }
    } catch (error) {
      // Final fallback: Download as image
      try {
        const element = templateRef.current;
        if (!element) {
          throw new Error("Template element not available");
        }

        const canvas = await html2canvas(element, {
          useCORS: true,
          allowTaint: false,
          background: null, // Use null to preserve transparency and show logo/gradient
          scale: 1.2, // Reduced scale for smaller file size (was 2)
          width: element.scrollWidth,
          height: element.scrollHeight,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
          logging: false,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: (clonedDoc: Document) => {
            // Only set white background on the outer container, not inner elements
            const clonedElement = clonedDoc.body.firstElementChild as HTMLElement;
            if (clonedElement) {
              clonedElement.style.backgroundColor = "#ffffff";
              // Don't override inner container backgrounds to preserve logo/gradient
            }
          },
        } as any);

        const link = document.createElement("a");
        link.download = `marksheet-${student.firstName || student.studentName || "student"
          }-${student.lastName || ""}-${className}-${sessionYear}-${new Date().toISOString().split("T")[0]
          }.png`;
        link.href = canvas.toDataURL("image/png", 0.92); // Reduced quality for smaller file size
        link.click();

        alert(t("pdf_generation_failed_image_downloaded"));
      } catch (imageError) {
        alert(
          t("failed_to_download_pdf").replace(
            "{error}",
            error instanceof Error ? error.message : "Unknown error"
          )
        );
      }
    } finally {
      setIsDownloadingPdfFromImage(false);
    }
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        ref={templateRef}
        style={{
          ...mainBoxStyle,
          border: "1px solid #000",
          boxShadow: "0 0 0 1px #000",
        }}
      >
        {/* Transparent school logo background */}
        {schoolLogoUrl && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={schoolLogoUrl}
              alt="School Logo"
              style={{
                width: "60%",
                height: "auto",
                opacity: 0.2, // Very low opacity for subtle watermark effect
                objectFit: "contain",
                // filter: "grayscale(100%) brightness(1.2)", // Make it more subtle
              }}
            />
          </div>
        )}
        {/* Vertical gradient color bands - greenish, yellowish, reddish */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(to right, rgba(144, 238, 144, 0.15) 0%, rgba(255, 255, 153, 0.15) 50%, rgba(255, 160, 160, 0.15) 100%)",
          }}
        />
        {/* Content wrapper to ensure content appears above background logo */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            backgroundColor: "transparent", // Keep transparent to show logo and gradient
          }}
        >
          {/* Official Board Header */}
          <div style={boardHeaderStyle}>
            <div
              style={{ ...boardNameStyle, fontSize: "30px", marginTop: "5px" }}
            >
              <strong>{schoolName.toUpperCase()}</strong>
            </div>
            <div style={examTitleStyle}>{displayMarksheetType} MARKSHEET {sessionYear || "N/A"}</div>
            <div >DISE CODE: {diseCode}</div>
          </div>

          {/* School Details Table */}
          <table style={schoolDetailsTableStyle}>
            <tbody>
              <tr>
                <th style={schoolDetailsThStyle}>SCHOOL CODE</th>
                <th style={schoolDetailsThStyle}>ENROLLMENT NUMBER</th>
                <th style={schoolDetailsThStyle}>REGULAR/PRIVATE</th>
                <th style={schoolDetailsThStyle}>ROLL NUMBER</th>
              </tr>
              <tr>
                <td style={schoolDetailsTdStyle}>
                  {finalSchoolData?.schoolCode || "N/A"}
                </td>
                <td style={schoolDetailsTdStyle}>
                  {student.enrolmentNo?.toUpperCase() || "N/A"}
                </td>
                <td style={schoolDetailsTdStyle}>REGULAR</td>
                <td style={schoolDetailsTdStyle}>
                  {student.rollNumber?.toUpperCase() || "N/A"}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Student Details Section */}
          <div style={studentDetailsStyle}>
            {/* Student Info Row with Photo */}
            <div style={studentInfoRowStyle}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: "5px", fontSize: "20px" }}>
                  <strong>
                    STUDENT'S NAME:{" "}
                    {student.studentName?.toUpperCase() ||
                      `${student.firstName?.toUpperCase() || ""} ${student.lastName?.toUpperCase() || ""
                        }`.trim()}
                  </strong>
                </div>
                <div style={{ marginBottom: "5px", fontSize: "20px" }}>
                  <strong>
                    FATHER'S NAME: {student.fatherName?.toUpperCase()}
                  </strong>
                </div>
                <div style={{ marginBottom: "5px", fontSize: "20px" }}>
                  <strong>
                    MOTHER'S NAME: {student.motherName?.toUpperCase()}
                  </strong>
                </div>
                <div style={{ marginBottom: "5px", fontSize: "20px" }}>
                  <strong>
                    DATE OF BIRTH: {formatDateOfBirth(student.dateOfBirth)}
                  </strong>
                </div>
                <div style={{ marginBottom: "5px", fontSize: "20px" }}>
                  <strong>
                    CLASS: {classWithSectionDisplay.toUpperCase()}
                  </strong>
                </div>
              </div>

              {/* Student Photo */}
              <div style={studentPhotoContainerStyle}>
                {(() => {
                  if (photoLoading) {
                    return (
                      <div
                        data-ms-photo-state="loading"
                        style={{
                          width: "130px",
                          height: "155px",
                          border: "2px solid black",
                          // borderRadius: "4px",
                          backgroundColor: "#fff",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#3a5ca8",
                        }}
                      >
                        <span style={{ fontSize: "10px", fontWeight: "bold" }}>
                          Loading...
                        </span>
                      </div>
                    );
                  }

                  if (authenticatedImageUrl && !imageError) {
                    return (
                      <img
                        data-ms-photo-state="ready"
                        src={authenticatedImageUrl}
                        alt="Student Photo"
                        style={studentPhotoStyle}
                      />
                    );
                  }

                  return (
                    <div
                      data-ms-photo-state="fallback"
                      style={{
                        width: "100px",
                        height: "150px",
                        border: "1px solid black",
                        borderRadius: "4px",
                        backgroundColor: "transparent",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "black",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "10px",
                          color: "black",
                          fontWeight: "bold",
                          lineHeight: 1,
                        }}
                      >
                        Photo
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Marks Table - Official Format */}
          {marksheetType === "FINAL" && className && (className.toLowerCase().includes("11th") || className.toLowerCase().includes("12th")) ? (
            // 11th / 12th Class FINAL Marksheet — subject order from subject-mark-mapping priority
            <table style={marksTableStyle}>
              <tbody>
                {/* Header Row 1 */}
                <tr>
                  <th style={marksThStyle} rowSpan={3}>
                    विषय / SUBJECT
                  </th>
                  <th style={marksThStyle} colSpan={2} rowSpan={2}>
                    अधिकतम अंक / MAXIMUM MARKS
                  </th>
                  <th style={marksThStyle} colSpan={2} rowSpan={2}>
                    न्यूनतम अंक / MINIMUM MARKS
                  </th>
                  <th style={marksThStyle} colSpan={2}>
                    त्रैमासिक परीक्षा / QUARTERLY EXAM
                  </th>
                  <th style={marksThStyle} colSpan={2}>
                    अर्द्धवार्षिक परीक्षा / HALF YEARLY EXAM
                  </th>
                  <th style={marksThStyle} colSpan={2}>
                    वार्षिक परीक्षा / ANNUAL EXAM
                  </th>
                  <th
                    style={{
                      ...marksThStyle,
                      width: "auto",
                      minWidth: "200px",
                      border: "1px solid #000",
                    }}
                    colSpan={3}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1.3,
                        padding: "8px 12px",
                        width: "100%",
                      }}
                    >
                      <span style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "3px" }}>
                        अधिभार आधारित वार्षिक परीक्षाफल
                      </span>
                      <span style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "4px" }}>
                        WEIGHTAGE BASED RESULT
                      </span>
                      <span style={{ fontSize: "9px", marginTop: "2px", marginBottom: "1px", textAlign: "center" }}>
                        (त्रैमासिक 5% + अर्द्धवार्षिक 5% + वार्षिक 90% = 100%)
                      </span>
                      <span style={{ fontSize: "9px", textAlign: "center" }}>
                        (QUARTERLY EXAM 5% + HALF YEARLY EXAM 5% + ANNUAL EXAM 90% = 100%)
                      </span>
                    </div>
                  </th>
                  <th
                    style={{
                      ...marksThStyle,
                      verticalAlign: "middle",
                      textAlign: "center",
                      border: "1px solid #000",
                    }}
                    rowSpan={3}
                  >
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      lineHeight: 1.4,
                    }}>
                      <span style={{ fontWeight: "bold", fontSize: "11px" }}>
                        विशेष
                      </span>
                      <span style={{ fontWeight: "bold", fontSize: "11px" }}>
                        REMARKS
                      </span>
                    </div>
                  </th>
                </tr>
                {/* Header Row 2 */}
                <tr>
                  <th style={marksThStyle} colSpan={2}>
                    प्राप्तांक / OBTAINED MARKS
                  </th>
                  <th style={marksThStyle} colSpan={2}>
                    प्राप्तांक / OBTAINED MARKS
                  </th>
                  <th style={marksThStyle} colSpan={2}>
                    प्राप्तांक / OBTAINED MARKS
                  </th>
                  <th
                    style={{
                      ...marksThStyle,
                      border: "1px solid #000",
                    }}
                    colSpan={3}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1.3,
                        padding: "6px 4px",
                      }}
                    >
                      <span style={{ fontWeight: "bold", fontSize: "10px", marginBottom: "2px" }}>
                        अधिभार का योग (IN ROUND)
                      </span>
                      <span style={{ fontWeight: "bold", fontSize: "10px" }}>
                        SUM OF WEIGHTAGE
                      </span>
                    </div>
                  </th>
                </tr>
                {/* Header Row 3 */}
                <tr>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>TH</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>PR</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>TH</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>PR</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>TH</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>PR</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>TH</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>PR</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>TH</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>PR</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>TH</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>PR</th>
                  <th style={{ ...marksThStyle, border: "1px solid #000" }}>TOTAL</th>
                </tr>
                {/* Separate regular subjects from NSQF/ADDITIONAL GROUP subjects */}
                {(() => {
                  // Use API data to determine group membership
                  const regularSubjects = subjects.filter((subj) => {
                    // First check API mapping, then fallback to subject property
                    const codeKey = normalizeSubjectCodeForMapping(subj.subjectCode);
                    const groupNameFromAPI = codeKey ? subjectGroupMap.get(codeKey) : undefined;
                    const groupName = groupNameFromAPI || (subj as any).groupName || "";
                    return groupName !== "NSQF GROUP" && groupName !== "ADDITIONAL GROUP";
                  });

                  const nsqfAdditionalSubjects = subjects.filter((subj) => {
                    const codeKey = normalizeSubjectCodeForMapping(subj.subjectCode);
                    const groupNameFromAPI = codeKey ? subjectGroupMap.get(codeKey) : undefined;
                    const groupName = groupNameFromAPI || (subj as any).groupName || "";
                    return groupName === "NSQF GROUP" || groupName === "ADDITIONAL GROUP";
                  });

                  // Helper function to render a subject row
                  const renderSubjectRow = (subj: Subject, idx: number) => {
                    const maxMarksInfo = getCustomMaxMarks(subj.subjectName, marksheetType, subj.subjectCode);
                    const maxTheory = maxMarksInfo.theory;
                    const maxPractical = maxMarksInfo.practical;
                    const minTheory = maxMarksInfo.minTheory;
                    const minPractical = maxMarksInfo.minPractical;

                    // Check if subject is vocational strictly using API mapping data (subjectType or subject_type)
                    const subjCodeKey = normalizeSubjectCodeForMapping(subj.subjectCode);
                    const mapping = subjCodeKey ? subjectMarksConfigMap.get(subjCodeKey) : null;
                    const isVocationalSubject = mapping?.subjectType === "VOCATIONAL" || mapping?.subject_type === "VOCATIONAL";

                    // Check if subject is from NSQF/ADDITIONAL GROUP using API data
                    const groupNameFromAPI = mapping?.groupName || (subjCodeKey ? subjectGroupMap.get(subjCodeKey) : undefined);
                    const groupName = groupNameFromAPI || (subj as any).groupName || "";
                    const isNsqfAdditionalGroup = groupName === "NSQF GROUP" || groupName === "ADDITIONAL GROUP";

                    // Get marks from quarterly, half-yearly, and annual marksheets
                    const quarterlyTheory = subj.quarterlyTheoryMarks != null ? subj.quarterlyTheoryMarks : null;
                    const quarterlyPractical = subj.quarterlyPracticalMarks != null ? subj.quarterlyPracticalMarks : null;
                    const halfYearlyTheory = subj.halfYearlyTheoryMarks != null ? subj.halfYearlyTheoryMarks : null;
                    const halfYearlyPractical = subj.halfYearlyPracticalMarks != null ? subj.halfYearlyPracticalMarks : null;
                    const annualTheory = subj.theoryMarks != null ? subj.theoryMarks : null;
                    const annualPractical = subj.practicalMarks != null ? subj.practicalMarks : null;

                    // Weightage values from FINAL marksheet
                    const quarterlyWeightageValue = subj.quarterlyWeightage != null ? subj.quarterlyWeightage : null;
                    const halfYearlyWeightageValue = subj.halfYearlyWeightage != null ? subj.halfYearlyWeightage : null;
                    const totalMarks = subj.totalMarks != null ? subj.totalMarks : null;

                    // TH column uses quarterlyWeightage, PR column uses halfYearlyWeightage
                    // Vocational subjects have empty weightage columns; NSQF/ADDITIONAL subjects show weightage
                    // Show numeric values with fixed precision (avoid long floating decimals)
                    const weightageTH = isVocationalSubject
                      ? null
                      : (quarterlyWeightageValue != null
                        ? Number(Number(quarterlyWeightageValue).toFixed(2))
                        : null);
                    const weightagePR = isVocationalSubject
                      ? null
                      : (halfYearlyWeightageValue != null
                        ? Number(Number(halfYearlyWeightageValue).toFixed(2))
                        : null);

                    return (
                      <tr key={`${subj.subjectCode}-${idx}`}>
                        <td style={subjectTdStyle}>
                          {subj.subjectCode?.toUpperCase()}{" "}
                          {subj.subjectName?.toUpperCase()}
                        </td>
                        {/* MAXIMUM MARKS (TH / PR) - always show for NSQF/Additional subjects */}
                        <td style={marksTdStyle}>{(isVocationalSubject && !isNsqfAdditionalGroup) ? "" : maxTheory}</td>
                        <td style={marksTdStyle}>{(isVocationalSubject && !isNsqfAdditionalGroup) ? "" : maxPractical}</td>
                        {/* MINIMUM MARKS (TH / PR) - always show for NSQF/Additional subjects */}
                        <td style={marksTdStyle}>
                          {(!isVocationalSubject || isNsqfAdditionalGroup) && minTheory != null ? minTheory : ""}
                        </td>
                        <td style={marksTdStyle}>
                          {(!isVocationalSubject || isNsqfAdditionalGroup) && minPractical != null ? minPractical : ""}
                        </td>
                        {/* QUARTERLY EXAM */}
                        <td style={marksTdStyle}>
                          {quarterlyTheory != null ? renderMark(quarterlyTheory as number) : ""}
                        </td>
                        <td style={marksTdStyle}>
                          {quarterlyPractical != null ? renderMark(quarterlyPractical as number) : ""}
                        </td>
                        {/* HALF YEARLY EXAM */}
                        <td style={marksTdStyle}>
                          {halfYearlyTheory != null ? renderMark(halfYearlyTheory as number) : ""}
                        </td>
                        <td style={marksTdStyle}>
                          {halfYearlyPractical != null ? renderMark(halfYearlyPractical as number) : ""}
                        </td>
                        {/* ANNUAL EXAM */}
                        <td style={marksTdStyle}>
                          {annualTheory != null ? renderMark(annualTheory as number) : ""}
                        </td>
                        <td style={marksTdStyle}>
                          {annualPractical != null ? renderMark(annualPractical as number) : ""}
                        </td>
                        {/* WEIGHTAGE BASED RESULT */}
                        <td style={marksTdStyle}>
                          {weightageTH != null ? weightageTH : ""}
                        </td>
                        <td style={marksTdStyle}>
                          {weightagePR != null ? weightagePR : ""}
                        </td>
                        <td style={marksTdStyle}>
                          {totalMarks != null ? totalMarks : ""}
                        </td>
                        {/* REMARKS */}
                        <td style={marksTdStyle}>{subj.remark || ""}</td>
                      </tr>
                    );
                  };

                  return (
                    <>
                      {/* Regular Subjects */}
                      {regularSubjects.map((subj, idx) => renderSubjectRow(subj, idx))}
                      {/* Total Row - Only includes regular subjects */}
                      <tr style={totalRowStyle}>
                        <td style={subjectTdStyle}>TOTAL</td>
                        {/* MAX MARKS TOTAL (TH / PR) */}
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const cfgKey = normalizeSubjectCodeForMapping(s.subjectCode);
                            const cfg =
                              cfgKey && subjectMarksConfigMap.get(cfgKey);
                            const th =
                              cfg && cfg.theoryMarks != null
                                ? Number(cfg.theoryMarks)
                                : getCustomMaxMarks(s.subjectName, marksheetType).theory;
                            return sum + th;
                          }, 0)}
                        </td>
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const cfgKey = normalizeSubjectCodeForMapping(s.subjectCode);
                            const cfg =
                              cfgKey && subjectMarksConfigMap.get(cfgKey);
                            const pr =
                              cfg && cfg.practicalMarks != null
                                ? Number(cfg.practicalMarks)
                                : getCustomMaxMarks(s.subjectName, marksheetType).practical;
                            return sum + pr;
                          }, 0)}
                        </td>
                        {/* MINIMUM MARKS TOTAL (TH / PR) */}
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const cfgKey = normalizeSubjectCodeForMapping(s.subjectCode);
                            const cfg =
                              cfgKey && subjectMarksConfigMap.get(cfgKey);
                            const thPass =
                              cfg && cfg.thPassMarks != null
                                ? Number(cfg.thPassMarks)
                                : 0;
                            return sum + thPass;
                          }, 0)}
                        </td>
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const cfgKey = normalizeSubjectCodeForMapping(s.subjectCode);
                            const cfg =
                              cfgKey && subjectMarksConfigMap.get(cfgKey);
                            const prPass =
                              cfg && cfg.prPassMarks != null
                                ? Number(cfg.prPassMarks)
                                : 0;
                            return sum + prPass;
                          }, 0)}
                        </td>
                        {/* QUARTERLY EXAM TOTAL */}
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const val = s.quarterlyTheoryMarks;
                            return sum + (val != null && val !== -1 ? Number(val) : 0);
                          }, 0)}
                        </td>
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const val = s.quarterlyPracticalMarks;
                            return sum + (val != null && val !== -1 ? Number(val) : 0);
                          }, 0)}
                        </td>
                        {/* HALF YEARLY EXAM TOTAL */}
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const val = s.halfYearlyTheoryMarks;
                            return sum + (val != null && val !== -1 ? Number(val) : 0);
                          }, 0)}
                        </td>
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const val = s.halfYearlyPracticalMarks;
                            return sum + (val != null && val !== -1 ? Number(val) : 0);
                          }, 0)}
                        </td>
                        {/* ANNUAL EXAM TOTAL */}
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const val = s.theoryMarks;
                            return sum + (val != null && val !== -1 ? Number(val) : 0);
                          }, 0)}
                        </td>
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            const val = s.practicalMarks;
                            return sum + (val != null && val !== -1 ? Number(val) : 0);
                          }, 0)}
                        </td>
                        {/* WEIGHTAGE BASED RESULT TOTAL (fixed decimal precision) */}
                        <td style={marksTdStyle}>
                          {Number(
                            regularSubjects
                              .reduce((sum, s) => {
                                const ck = normalizeSubjectCodeForMapping(s.subjectCode);
                                const cfg = ck ? subjectMarksConfigMap.get(ck) : null;
                                const isVoc = cfg?.subjectType === "VOCATIONAL" || cfg?.subject_type === "VOCATIONAL";
                                if (isVoc) return sum;
                                const qw = s.quarterlyWeightage || 0;
                                return sum + Number(qw);
                              }, 0)
                              .toFixed(2)
                          )}
                        </td>
                        <td style={marksTdStyle}>
                          {Number(
                            regularSubjects
                              .reduce((sum, s) => {
                                const ck = normalizeSubjectCodeForMapping(s.subjectCode);
                                const cfg = ck ? subjectMarksConfigMap.get(ck) : null;
                                const isVoc = cfg?.subjectType === "VOCATIONAL" || cfg?.subject_type === "VOCATIONAL";
                                if (isVoc) return sum;
                                const hw = s.halfYearlyWeightage || 0;
                                return sum + Number(hw);
                              }, 0)
                              .toFixed(2)
                          )}
                        </td>
                        <td style={marksTdStyle}>
                          {regularSubjects.reduce((sum, s) => {
                            return sum + (s.totalMarks || 0);
                          }, 0)}
                        </td>
                        {/* REMARKS */}
                        <td style={marksTdStyle}></td>
                      </tr>
                      {/* Spacer row between TOTAL and NSQF/ADDITIONAL GROUP subjects */}
                      {nsqfAdditionalSubjects.length > 0 && (
                        <tr>
                          <td
                            colSpan={14}
                            style={{
                              border: "none",
                              height: "8px",
                              padding: "2px 4px",
                              background: "transparent",
                              textAlign: "left",
                              fontStyle: "italic",
                              fontWeight: "bold",
                              fontSize: "11px",
                            }}
                          >
                            Additional Subject
                          </td>
                        </tr>
                      )}
                      {/* NSQF/ADDITIONAL GROUP Subjects - Displayed after TOTAL row */}
                      {nsqfAdditionalSubjects.map((subj, idx) => renderSubjectRow(subj, idx))}
                    </>
                  );
                })()}
              </tbody>
            </table>
          ) : is6thOr7th && marksheetType === "ANNUAL" ? (
            // 6th and 7th Class ANNUAL Marksheet - Custom Column Order
            <table style={marksTableStyle}>
              <tbody>
                {/* Header Row 1 */}
                <tr>
                  <th style={marksThStyle} rowSpan={2}>
                    विषय / SUBJECT
                  </th>
                  <th style={marksThStyle} colSpan={3}>
                    अधिकतम अंक / MAXIMUM MARKS
                  </th>
                  <th style={marksThStyle} colSpan={2}>
                    प्राप्तांक / OBTAINED MARKS
                  </th>
                  <th style={marksThStyle} colSpan={4}>
                    अधिभार / WEIGHTAGE MARKS <p>(10% QR + 10% HY + 80% AN)</p>
                  </th>
                  <th style={marksThStyle} rowSpan={2}>
                    ग्रेड / GRADE
                  </th>
                  <th style={marksThStyle} rowSpan={2}>
                    टिप्पणी / REMARK
                  </th>
                </tr>
                {/* Header Row 2 */}
                <tr>
                  <th style={marksThStyle}>TH</th>
                  <th style={marksThStyle}>PR</th>
                  <th style={marksThStyle}>TOTAL</th>
                  <th style={marksThStyle}>TH</th>
                  <th style={marksThStyle}>PR</th>
                  <th style={marksThStyle}>10% QR</th>
                  <th style={marksThStyle}>10% HY</th>
                  <th style={marksThStyle}>THEORY TOTAL</th>
                  <th style={marksThStyle}>TOTAL</th>
                </tr>
                {subjects.map((subj, idx) => {
                  const maxMarks = getCustomMaxMarks(subj.subjectName, marksheetType, subj.subjectCode);

                  // Obtained marks
                  const annualTheory = subj.theoryMarks != null && subj.theoryMarks !== -1 ? subj.theoryMarks : 0;
                  const annualPractical = subj.practicalMarks != null && subj.practicalMarks !== -1 ? subj.practicalMarks : 0;
                  const annualTotal = annualTheory + annualPractical;

                  // Weightage marks
                  const quarterlyWeight = subj.quarterlyWeightage != null ? Number(subj.quarterlyWeightage).toFixed(2) : "";
                  const halfYearlyWeight = subj.halfYearlyWeightage != null ? Number(subj.halfYearlyWeightage).toFixed(2) : "";
                  const annualWeight = subj.theoryMarks != null && subj.theoryMarks !== -1 ? subj.theoryMarks : "";
                  const weightTotal = subj.totalMarks != null ? subj.totalMarks : "";

                  return (
                    <tr key={idx}>
                      <td style={subjectTdStyle}>
                        {subj.subjectCode?.toUpperCase()} {subj.subjectName?.toUpperCase()}
                      </td>
                      {/* MAXIMUM MARKS */}
                      <td style={marksTdStyle}>{maxMarks.theory}</td>
                      <td style={marksTdStyle}>{maxMarks.practical}</td>
                      <td style={marksTdStyle}>{maxMarks.total}</td>
                      {/* OBTAINED MARKS */}
                      <td style={marksTdStyle}>{renderMark(subj.originalPracticalMarks)}</td>
                      <td style={marksTdStyle}>{renderMark(subj.practicalMarks)}</td>
                      {/* WEIGHTAGE MARKS */}
                      <td style={marksTdStyle}>{quarterlyWeight}</td>
                      <td style={marksTdStyle}>{halfYearlyWeight}</td>
                      <td style={marksTdStyle}>{annualWeight}</td>
                      <td style={marksTdStyle}>{weightTotal}</td>
                      {/* GRADE & REMARK */}
                      <td style={marksTdStyle}>{subj.grade || ""}</td>
                      <td style={marksTdStyle}>{subj.remark || ""}</td>
                    </tr>
                  );
                })}
                {/* Total Row */}
                <tr style={totalRowStyle}>
                  <td style={subjectTdStyle}>TOTAL</td>
                  <td style={marksTdStyle}>{totalTheoryMax}</td>
                  <td style={marksTdStyle}>{totalPracticalMax}</td>
                  <td style={marksTdStyle}>{totalMax}</td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, s) => sum + (s.originalPracticalMarks || 0), 0)}
                  </td>
                  <td style={marksTdStyle}>
                    {totalPracticalObtained !== undefined && totalPracticalObtained !== null ? totalPracticalObtained : totalPractical}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, s) => sum + (s.quarterlyWeightage || 0), 0).toFixed(2)}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, s) => sum + (s.halfYearlyWeightage || 0), 0).toFixed(2)}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, s) => {
                      const val = s.theoryMarks != null && s.theoryMarks !== -1 ? s.theoryMarks : 0;
                      return sum + val;
                    }, 0)}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, s) => sum + (s.totalMarks || 0), 0)}
                  </td>
                  <td style={marksTdStyle}></td>
                  <td style={marksTdStyle}></td>
                </tr>
              </tbody>
            </table>
          ) : marksheetType === "FINAL" ? (
            <table style={marksTableStyle}>
              <tbody>
                {/* Header Row 1 - includes weightage band over 5%/90%/TOTAL MARKS columns */}
                <tr>
                  <th style={marksThStyle} rowSpan={2}>
                    SUBJECT
                  </th>
                  <th style={marksThStyle} rowSpan={2}>
                    MAX MARKS
                  </th>
                  <th style={{ ...marksThStyle, position: "relative", width: "40px" }} rowSpan={2}>
                    <div style={{
                      transform: "translate(-50%, -50%) rotate(-90deg)",
                      whiteSpace: "nowrap",
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transformOrigin: "center"
                    }}>
                      QUARTERLY TOTAL
                    </div>
                  </th>
                  <th style={{ ...marksThStyle, position: "relative", width: "40px" }} rowSpan={2}>
                    <div style={{
                      transform: "translate(-50%, -50%) rotate(-90deg)",
                      whiteSpace: "nowrap",
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transformOrigin: "center"
                    }}>
                      HALF YEARLY TOTAL
                    </div>
                  </th>
                  <th style={marksThStyle} colSpan={3}>
                    ANNUAL EXAMINATION
                  </th>
                  <th style={marksThStyle} colSpan={4}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1.4,
                      }}
                    >
                      <span style={{ fontWeight: "bold", fontSize: "11px" }}>
                        WEIGHTAGE BASED RESULT
                      </span>
                      <span style={{ fontSize: "10px" }}>
                        VOCATIONAL SUB. = 100% OF ANNUAL
                      </span>
                      <span style={{ fontSize: "10px" }}>
                        GENERAL SUB. = (QR. 5% + HY. 5% + ANNUAL 90% = 100%)
                      </span>
                    </div>
                  </th>
                  <th style={marksThStyle} rowSpan={2}>
                    GRADE
                  </th>
                  <th style={marksThStyle} rowSpan={2}>
                    REMARK
                  </th>
                </tr>
                {/* Header Row 2 - annual exam + individual weightage columns */}
                <tr>
                  <th style={marksThStyle}>TH</th>
                  <th style={marksThStyle}>PR</th>
                  <th style={marksThStyle}>TOTAL</th>
                  <th style={marksThStyle}>5% QUARTERLY</th>
                  <th style={marksThStyle}>5% HALF YEARLY</th>
                  <th style={marksThStyle}>90% ANNUAL</th>
                  <th style={marksThStyle}>TOTAL MARKS</th>
                </tr>
                {subjects.map((subj, idx) => {
                  const maxMarks = getCustomMaxMarks(subj.subjectName, marksheetType, subj.subjectCode);

                  // Check if subject is vocational using API mapping subject_type
                  const apiKey = normalizeSubjectCodeForMapping(subj.subjectCode);
                  const apiMapping = apiKey ? subjectMarksConfigMap.get(apiKey) : null;
                  const isVocationalSubject = apiMapping?.subjectType === "VOCATIONAL" || apiMapping?.subject_type === "VOCATIONAL";

                  // Use quarterlyTotal and halfYearlyTotal directly from the subject object (set by buildFinalSubjectsWithExamTotals)
                  // These come from QUARTERLY and HALF_YEARLY marksheets - total marks (theory + practical) for each subject
                  const quarterlyTotal = (subj as any).quarterlyTotal != null && (subj as any).quarterlyTotal !== undefined
                    ? (subj as any).quarterlyTotal
                    : null;
                  const halfYearlyTotal = (subj as any).halfYearlyTotal != null && (subj as any).halfYearlyTotal !== undefined
                    ? (subj as any).halfYearlyTotal
                    : null;

                  const annualTotal =
                    (subj.theoryMarks && subj.theoryMarks > 0 ? subj.theoryMarks : 0) +
                    (subj.practicalMarks && subj.practicalMarks > 0 ? subj.practicalMarks : 0);

                  // For weightage columns: use values directly from FINAL marksheet object (no calculations)
                  // 5% QUARTERLY: use quarterlyWeightage as-is from FINAL marksheet (e.g., 2.85)
                  // 5% HALF YEARLY: use halfYearlyWeightage as-is from FINAL marksheet (e.g., 2.3)
                  // 90% ANNUAL: use originalPracticalMarks as-is from FINAL marksheet (e.g., 41)
                  const quarterlyWeightageValue = subj.quarterlyWeightage != null ? subj.quarterlyWeightage : null;
                  const halfYearlyWeightageValue = subj.halfYearlyWeightage != null ? subj.halfYearlyWeightage : null;
                  const annualWeightageValue = subj.originalPracticalMarks != null ? subj.originalPracticalMarks : null;
                  const totalMarks = subj.totalMarks != null ? subj.totalMarks : null;

                  return (
                    <tr key={idx}>
                      <td style={subjectTdStyle}>
                        {subj.subjectCode?.toUpperCase()}{" "}
                        {subj.subjectName?.toUpperCase()}
                      </td>
                      <td style={marksTdStyle}>{maxMarks.total}</td>
                      <td style={marksTdStyle}>
                        {quarterlyTotal != null && quarterlyTotal !== undefined
                          ? Math.round(Number(quarterlyTotal))
                          : ""}
                      </td>
                      <td style={marksTdStyle}>
                        {halfYearlyTotal != null && halfYearlyTotal !== undefined
                          ? Math.round(Number(halfYearlyTotal))
                          : ""}
                      </td>
                      <td style={marksTdStyle}>
                        {renderMark(subj.theoryMarks as number)}
                      </td>
                      <td style={marksTdStyle}>
                        {renderMark(subj.practicalMarks as number)}
                      </td>
                      <td style={marksTdStyle}>{annualTotal}</td>
                      <td style={marksTdStyle}>
                        {isVocationalSubject
                          ? ""
                          : (quarterlyWeightageValue != null ? Number(quarterlyWeightageValue).toFixed(2) : "")}
                      </td>
                      <td style={marksTdStyle}>
                        {isVocationalSubject
                          ? ""
                          : (halfYearlyWeightageValue != null ? Number(halfYearlyWeightageValue).toFixed(2) : "")}
                      </td>
                      <td style={marksTdStyle}>
                        {isVocationalSubject
                          ? ""
                          : (annualWeightageValue != null ? annualWeightageValue : "")}
                      </td>
                      <td style={marksTdStyle}>
                        {totalMarks != null ? totalMarks : ""}
                      </td>
                      <td style={marksTdStyle}>{subj.grade || ""}</td>
                      <td style={marksTdStyle}>{subj.remark || ""}</td>
                    </tr>
                  );
                })}
                <tr style={totalRowStyle}>
                  <td style={subjectTdStyle}>TOTAL</td>
                  <td style={marksTdStyle}>{totalMax}</td>
                  <td style={marksTdStyle}>{Math.round(totalQuarterly)}</td>
                  <td style={marksTdStyle}>{Math.round(totalHalfYearly)}</td>
                  <td style={marksTdStyle}>
                    {totalTheoryObtained !== undefined && totalTheoryObtained !== null
                      ? totalTheoryObtained
                      : totalTheory}
                  </td>
                  <td style={marksTdStyle}>
                    {totalPracticalObtained !== undefined && totalPracticalObtained !== null
                      ? totalPracticalObtained
                      : totalPractical}
                  </td>
                  <td style={marksTdStyle}>
                    {totalObtained !== undefined && totalObtained !== null
                      ? totalObtained
                      : totalObt}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, subj) => {
                      const normalizedSubjectName = subj.subjectName?.toLowerCase().trim() || "";
                      const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
                      if (isVocationalSubject) return sum;
                      return sum + (subj.quarterlyWeightage || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, subj) => {
                      const normalizedSubjectName = subj.subjectName?.toLowerCase().trim() || "";
                      const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
                      if (isVocationalSubject) return sum;
                      return sum + (subj.halfYearlyWeightage || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, subj) => {
                      const normalizedSubjectName = subj.subjectName?.toLowerCase().trim() || "";
                      const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
                      if (isVocationalSubject) return sum;
                      return sum + (subj.originalPracticalMarks || 0);
                    }, 0)}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, subj) => {
                      return sum + (subj.totalMarks || 0);
                    }, 0)}
                  </td>
                  <td style={marksTdStyle}></td>
                  <td style={marksTdStyle}></td>
                </tr>
              </tbody>
            </table>
          ) : className && className.toLowerCase().includes("9th") && marksheetType === "ANNUAL" ? (
            // 9th Class Special Table Structure
            <table style={marksTableStyle}>
              <tbody>
                {/* Header Row 1 */}
                <tr>
                  <th style={marksThStyle} rowSpan={3}>
                    विषय / SUBJECT
                  </th>
                  <th style={marksThStyle} colSpan={2} rowSpan={2}>
                    अधिकतम अंक / MAXIMUM MARKS
                  </th>
                  <th style={marksThStyle} rowSpan={3}>
                    वार्षिक परीक्षा प्राप्तांक सैद्धांतिक / ANNUAL OBTAINED MARKS THEORY
                  </th>
                  <th style={marksThStyle} colSpan={3}>
                    प्रायोगिक/प्रोजेक्ट / PRACTICAL/PROJECT
                  </th>
                  <th style={marksThStyle} colSpan={2} rowSpan={2}>
                    TOTAL (IN ROUND)
                  </th>
                  <th style={marksThStyle} rowSpan={3}>
                    GRAND TOTAL
                  </th>
                  <th style={marksThStyle} rowSpan={3}>
                    REMARK
                  </th>
                </tr>
                {/* Header Row 2 */}
                <tr>
                  <th style={marksThStyle} rowSpan={2}>
                    वार्षिक परीक्षा प्राप्तांक प्रायोगिक / प्रोजेक्ट (I) / ANNUAL OBTAINED MARKS PRACTICAL/PROJECT (I)
                  </th>
                  <th style={marksThStyle} rowSpan={2}>
                    त्रैमासिक परीक्षा का 5% अधिभार (TH+PR) (II) / 5% WEIGHTAGE OF QUARTERLY EXAM (TH+PR) (II)
                  </th>
                  <th style={marksThStyle} rowSpan={2}>
                    अर्दधवार्षिक परीक्षा का 5% अधिभार (TH+PR) (III) / 5% WEIGHTAGE OF HALF YEARLY EXAM (TH+PR) (III)
                  </th>
                </tr>
                {/* Header Row 3 */}
                <tr>
                  <th style={marksThStyle}>TH</th>
                  <th style={marksThStyle}>PR</th>
                  <th style={marksThStyle}>TH</th>
                  <th style={marksThStyle}>PR</th>
                </tr>
                {/* Data Rows */}
                {subjects.map((subj, idx) => {
                  const maxMarks = getCustomMaxMarks(
                    subj.subjectName,
                    marksheetType,
                    subj.subjectCode
                  );

                  // Check if subject is vocational
                  const normalizedSubjectName = subj.subjectName?.toLowerCase().trim() || "";
                  const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;

                  // For column (I), use originalPracticalMarks for non-vocational subjects,
                  // and practicalMarks for vocational subjects (as requested)
                  const practicalColumnValue = isVocationalSubject
                    ? (subj.practicalMarks !== null && subj.practicalMarks !== undefined ? subj.practicalMarks : null)
                    : (subj.originalPracticalMarks !== null && subj.originalPracticalMarks !== undefined ? subj.originalPracticalMarks : null);

                  // For TOTAL (IN ROUND) PR column, use actual practicalMarks from backend (no calculation)
                  const actualPracticalMarks = subj.practicalMarks !== null && subj.practicalMarks !== undefined
                    ? subj.practicalMarks
                    : null;

                  // GRAND TOTAL = theoryMarks + actualPracticalMarks
                  const grandTotal = (subj.theoryMarks || 0) + (actualPracticalMarks || 0);

                  return (
                    <tr key={idx}>
                      <td style={subjectTdStyle}>
                        ({subj.subjectCode}) {subj.subjectName?.toUpperCase()}
                      </td>
                      {/* Maximum Marks */}
                      <td style={marksTdStyle}>{maxMarks.theory}</td>
                      <td style={marksTdStyle}>{maxMarks.practical}</td>
                      {/* Annual Obtained Marks Theory */}
                      <td style={marksTdStyle}>
                        {renderMark(subj.theoryMarks)}
                      </td>
                      {/* Practical/Project Columns - Column (I): originalPracticalMarks for non-vocational, practicalMarks for vocational */}
                      <td style={marksTdStyle}>
                        {practicalColumnValue !== null && practicalColumnValue !== undefined
                          ? renderMark(practicalColumnValue)
                          : ""}
                      </td>
                      {/* 5% Weightage Columns - Show empty for vocational subjects */}
                      <td style={marksTdStyle}>
                        {isVocationalSubject
                          ? ""
                          : (subj.quarterlyWeightage !== undefined && subj.quarterlyWeightage !== null
                            ? subj.quarterlyWeightage.toFixed(2)
                            : "")}
                      </td>
                      <td style={marksTdStyle}>
                        {isVocationalSubject
                          ? ""
                          : (subj.halfYearlyWeightage !== undefined && subj.halfYearlyWeightage !== null
                            ? subj.halfYearlyWeightage.toFixed(2)
                            : "")}
                      </td>
                      {/* TOTAL (IN ROUND) */}
                      <td style={marksTdStyle}>
                        {renderMark(subj.theoryMarks)}
                      </td>
                      <td style={marksTdStyle}>
                        {actualPracticalMarks !== null && actualPracticalMarks !== undefined
                          ? renderMark(actualPracticalMarks)
                          : ""}
                      </td>
                      {/* GRAND TOTAL */}
                      <td style={marksTdStyle}>
                        {grandTotal}
                      </td>
                      {/* REMARK */}
                      <td style={marksTdStyle}>{subj.remark || ""}</td>
                    </tr>
                  );
                })}
                {/* Total Row */}
                <tr style={totalRowStyle}>
                  <td style={subjectTdStyle}>TOTAL</td>
                  <td style={marksTdStyle}>{totalTheoryMax}</td>
                  <td style={marksTdStyle}>{totalPracticalMax}</td>
                  <td style={marksTdStyle}>
                    {totalTheoryObtained !== undefined && totalTheoryObtained !== null
                      ? totalTheoryObtained
                      : totalTheory}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, subj) => {
                      // For column (I) total: use originalPracticalMarks for non-vocational, practicalMarks for vocational
                      const normalizedSubjectName = subj.subjectName?.toLowerCase().trim() || "";
                      const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
                      const value = isVocationalSubject
                        ? (subj.practicalMarks ?? 0)
                        : (subj.originalPracticalMarks ?? 0);
                      return sum + value;
                    }, 0)}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, subj) => {
                      // Exclude vocational subjects from weightage sum
                      const normalizedSubjectName = subj.subjectName?.toLowerCase().trim() || "";
                      const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
                      if (isVocationalSubject) return sum;
                      return sum + (subj.quarterlyWeightage || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, subj) => {
                      // Exclude vocational subjects from weightage sum
                      const normalizedSubjectName = subj.subjectName?.toLowerCase().trim() || "";
                      const isVocationalSubject = vocationalSubjects?.has(normalizedSubjectName) ?? false;
                      if (isVocationalSubject) return sum;
                      return sum + (subj.halfYearlyWeightage || 0);
                    }, 0).toFixed(2)}
                  </td>
                  <td style={marksTdStyle}>
                    {totalTheoryObtained !== undefined && totalTheoryObtained !== null
                      ? totalTheoryObtained
                      : totalTheory}
                  </td>
                  <td style={marksTdStyle}>
                    {subjects.reduce((sum, subj) => {
                      // Sum of actual practicalMarks from backend for TOTAL (PR) column
                      return sum + (subj.practicalMarks !== null && subj.practicalMarks !== undefined ? subj.practicalMarks : 0);
                    }, 0)}
                  </td>
                  <td style={marksTdStyle}>
                    {totalObtained !== undefined && totalObtained !== null
                      ? totalObtained
                      : totalObt}
                  </td>
                  <td style={marksTdStyle}></td>
                </tr>
              </tbody>
            </table>
          ) : (
            // Original table for other classes
            <table style={marksTableStyle}>
              <tbody>
                {marksheetType === "MONTHLY" ? (
                  // Monthly marksheet - simplified table
                  <>
                    <tr>
                      <th style={marksThStyle}>SUBJECT</th>
                      <th style={marksThStyle}>MAXIMUM MARKS</th>
                      <th style={marksThStyle}>MARKS OBTAINED</th>
                      <th style={marksThStyle}>GRADE</th>
                    </tr>
                  </>
                ) : (
                  // Non-monthly marksheet - full table
                  <>
                    <tr>
                      <th style={marksThStyle} rowSpan={2}>
                        SUBJECT
                      </th>
                      <th style={marksThStyle} colSpan={3}>
                        MAXIMUM MARKS
                      </th>
                      <th style={marksThStyle} colSpan={3}>
                        MARKS OBTAINED
                      </th>
                      <th style={marksThStyle} rowSpan={2}>
                        GRADE
                      </th>
                      <th style={marksThStyle} rowSpan={2}>
                        REMARKS
                      </th>
                    </tr>
                    <tr>
                      <th style={marksThStyle}>THEORY</th>
                      <th style={marksThStyle}>INTERNAL</th>
                      <th style={marksThStyle}>TOTAL</th>
                      <th style={marksThStyle}>THEORY</th>
                      <th style={marksThStyle}>INTERNAL</th>
                      <th style={marksThStyle}>TOTAL</th>
                    </tr>
                  </>
                )}
                {subjects.map((subj, idx) => {
                  const maxMarks = getCustomMaxMarks(
                    subj.subjectName,
                    marksheetType,
                    subj.subjectCode
                  );

                  return (
                    <tr key={idx}>
                      <td style={subjectTdStyle}>
                        {subj.subjectCode?.toUpperCase()}{" "}
                        {subj.subjectName?.toUpperCase()}
                      </td>
                      {marksheetType === "MONTHLY" ? (
                        // Monthly marksheet - simplified columns
                        <>
                          <td style={marksTdStyle}>{maxMarks.theory}</td>
                          <td style={marksTdStyle}>
                            {renderMark(subj.theoryMarks)}
                          </td>
                          <td style={marksTdStyle}>{subj.grade || ""}</td>
                        </>
                      ) : (
                        // Non-monthly marksheet - full columns
                        <>
                          <td style={marksTdStyle}>{maxMarks.theory}</td>
                          <td style={marksTdStyle}>{maxMarks.practical}</td>
                          <td style={marksTdStyle}>{maxMarks.total}</td>
                          <td style={marksTdStyle}>
                            {renderMark(subj.theoryMarks)}
                          </td>
                          <td style={marksTdStyle}>
                            {renderMark(subj.practicalMarks)}
                          </td>
                          <td style={marksTdStyle}>
                            {renderTotalMark(
                              subj.theoryMarks,
                              subj.practicalMarks,
                              subj.totalMarks
                            )}
                          </td>
                          <td style={marksTdStyle}>{subj.grade || ""}</td>
                          <td style={marksTdStyle}>{subj.remark || ""}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
                <tr style={totalRowStyle}>
                  <td style={subjectTdStyle}>TOTAL</td>
                  {marksheetType === "MONTHLY" ? (
                    // Monthly marksheet - simplified total row
                    <>
                      <td style={marksTdStyle}>{totalTheoryMax}</td>
                      <td style={marksTdStyle}>{totalTheory}</td>
                      <td style={marksTdStyle}></td>
                    </>
                  ) : (
                    // Non-monthly marksheet - full total row
                    <>
                      <td style={marksTdStyle}>{totalTheoryMax}</td>
                      <td style={marksTdStyle}>{totalPracticalMax}</td>
                      <td style={marksTdStyle}>{totalMax}</td>
                      <td style={marksTdStyle}>
                        {totalTheoryObtained !== undefined &&
                          totalTheoryObtained !== null
                          ? totalTheoryObtained
                          : totalTheory}
                      </td>
                      <td style={marksTdStyle}>
                        {totalPracticalObtained !== undefined &&
                          totalPracticalObtained !== null
                          ? totalPracticalObtained
                          : totalPractical}
                      </td>
                      <td style={marksTdStyle}>
                        {totalObtained !== undefined && totalObtained !== null
                          ? totalObtained
                          : totalObt}
                      </td>
                      <td style={marksTdStyle}></td>
                      <td style={marksTdStyle}></td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          )}

          {/* Summary Table */}
          <table style={summaryTableStyle}>
            <tbody>
              <tr>
                <th style={summaryThTdStyle}>PERCENTAGE</th>
                <th style={summaryThTdStyle}>TOTAL GRADE</th>
                <th style={summaryThTdStyle}>RESULT</th>
                <th style={summaryThTdStyle}>RANK</th>
                <th style={summaryThTdStyle}>FAIL SUBJECT</th>
              </tr>
              <tr>
                <td style={summaryThTdStyle}>{percentage}%</td>
                <td style={summaryThTdStyle}>
                  {formatTotalGradeForDisplay(totalGrade) || "N/A"}
                </td>
                <td style={summaryThTdStyle}>{result?.toUpperCase()}</td>
                <td style={summaryThTdStyle}>{rank && rank > 0 ? rank : ""}</td>
                <td style={summaryThTdStyle}>{failSubjects?.toUpperCase()}</td>
              </tr>
            </tbody>
          </table>

          {/* Signatures */}
          <div style={marksheetType === "FINAL" ? signatureStyleCompact : signatureStyle}>
            <div style={teacherSignatureStyle}>
              {getTeacherName()}
              <br />
              CLASS TEACHER
            </div>
            <div style={principalSignatureStyle}>
              {getPrincipalName()}
              <br />
              PRINCIPAL
            </div>
          </div>
        </div>{" "}
        {/* End content wrapper */}
      </div>

      {/* Download Buttons - Only show if not PDF and not hidden */}
      {!isPdf && !hideDownloadButton && (
        <Box sx={{ marginTop: "16px", textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Image Download Button */}
            <Button
              variant="contained"
              startIcon={<Image />}
              disabled={isDownloadingImage}
              onClick={downloadAsImage}
              sx={{
                padding: "12px 24px",
                fontSize: 14,
                fontWeight: 600,
                minWidth: "200px",
                position: "relative",
                backgroundColor: "#9c27b0",
                borderRadius: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: "0 4px 12px rgba(156, 39, 176, 0.3)",
                "&:hover": {
                  backgroundColor: "#7b1fa2",
                  boxShadow: "0 6px 16px rgba(156, 39, 176, 0.4)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
                transition: "all 0.3s ease-in-out",
              }}
            >
              {isDownloadingImage ? (
                <>
                  <Box
                    sx={{
                      display: "inline-block",
                      width: "16px",
                      height: "16px",
                      border: "2px solid #fff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginRight: "8px",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                  {t("generating")} {t("image")}...
                </>
              ) : (
                "Download as Image"
              )}
            </Button>

            {/* PDF Download Button */}
            <Button
              variant="contained"
              startIcon={<Download />}
              disabled={isDownloadingPdfFromImage}
              onClick={downloadAsPdfFromImage}
              sx={{
                padding: "12px 24px",
                fontSize: 14,
                fontWeight: 600,
                minWidth: "200px",
                position: "relative",
                backgroundColor: "#2196f3",
                borderRadius: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                "&:hover": {
                  backgroundColor: "#1976d2",
                  boxShadow: "0 6px 16px rgba(33, 150, 243, 0.4)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
                transition: "all 0.3s ease-in-out",
              }}
            >
              {isDownloadingPdfFromImage ? (
                <>
                  <Box
                    sx={{
                      display: "inline-block",
                      width: "16px",
                      height: "16px",
                      border: "2px solid #fff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginRight: "8px",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                  {t("generating")} PDF...
                </>
              ) : (
                "Download as PDF"
              )}
            </Button>
          </Box>

          {/* Loading progress indicators */}
          {(isDownloadingImage || isDownloadingPdfFromImage) && (
            <Box sx={{ marginTop: "12px" }}>
              <Typography variant="body2" color="text.secondary">
                {isDownloadingImage
                  ? t("please_wait_marksheet_image")
                  : t("please_wait_marksheet_pdf")}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default MarksheetTemplate;
